import { useState, type CSSProperties } from 'react';
import { useTranslations } from 'use-intl';
import {
  AvatarStack,
  Button,
  Caption,
  Card,
  DotSep,
  I,
  Icon,
  Label,
  Pill,
  Pressable,
  Row,
  Stack,
  VisuallyHidden,
} from '@leanin/ui';
import type { PollView, PollVoterView } from '@leanin/shared';

import styles from './PollCard.module.css';
import { pollTimeLeft } from './poll-time-left';
import { PollVotersModal } from './PollVotersModal';

function pct(count: number, total: number): number {
  return total === 0 ? 0 : Math.round((count / total) * 100);
}

const ENDS_IN_KEY = {
  day: 'poll.endsInDays',
  hour: 'poll.endsInHours',
  minute: 'poll.endsInMinutes',
} as const;

/** Per-row reveal stagger, so fills sweep top-to-bottom rather than all at once. */
const ROW_STAGGER_MS = 70;

/**
 * Read view of a post's single-choice poll — the "Bubble" design. Every
 * option is one rounded, full-row tap target that stays mounted in BOTH
 * states, driven by `poll.results` (the server reveals results only once the
 * viewer has voted, the poll has closed, or the viewer wrote it —
 * `isPollRevealed`):
 *
 *  - HIDDEN (`results === null`): empty rows with a selection ring.
 *  - REVEALED: each row's fill grows in place to its share, a check pops into
 *    the viewer's ring, and the percentage (plus, on a `public` poll, the
 *    previewed voters' faces) arrives just after. See PollCard.module.css.
 *
 * Options are toggle buttons (`aria-pressed`), not radios: a radio group
 * selects on arrow-key focus, which here would cast (and re-cast) votes just
 * by moving through the list. Tapping another option while the poll is open
 * changes the vote; tapping your own is a no-op. Rows use `aria-disabled`
 * (not `disabled`) while a vote is in flight so keyboard focus stays put.
 *
 * The header pills say when voting closes and whether votes are public —
 * the voter-facing disclosure BEFORE they vote. A persistent off-screen live
 * region announces the tally summary when results land.
 *
 * A revealed public poll with votes offers "See who voted" in the footer, opening
 * the full per-option voter roster. It sits outside the option rows, which
 * stay vote buttons (no interactive content nested inside them).
 */
export function PollCard({
  postId,
  poll,
  question,
  onVote,
  pending,
}: {
  postId: string;
  poll: PollView;
  /** The post body — the poll's accessible name for the option group. */
  question: string;
  onVote: (optionId: string) => void;
  pending: boolean;
}) {
  const t = useTranslations('Feed');

  const revealed = poll.results !== null;
  const total = poll.results?.totalVotes ?? 0;
  const tallyFor = (id: string) =>
    poll.results?.tallies.find((tally) => tally.optionId === id);
  // Only a public poll's tallies carry voters; an anonymous poll's tally type
  // has no voter field.
  const votersFor = (id: string): PollVoterView[] =>
    poll.visibility === 'public'
      ? (poll.results?.tallies.find((tally) => tally.optionId === id)?.voters ?? [])
      : [];
  // Highest tally, floored at 0 so an empty closed poll marks no leader. Ties
  // mark every co-leader — "Most votes" is a factual statement per option.
  const leadCount = revealed
    ? Math.max(0, ...poll.options.map((o) => tallyFor(o.id)?.voteCount ?? 0))
    : 0;

  const statusText = revealed
    ? poll.closed
      ? t('poll.closedTotal', { count: total })
      : t('poll.openTotal', { count: total })
    : t('poll.voteToReveal');

  // `closesAt` is on the wire; `Date.now()` is read here (the boundary) and
  // passed into the pure `pollTimeLeft`.
  const timeLeft = poll.closed ? null : pollTimeLeft(poll.closesAt, Date.now());
  const canChangeVote = !poll.closed && poll.viewerVotedOptionId !== null;
  const [votersOpen, setVotersOpen] = useState(false);
  const votersResults =
    poll.visibility === 'public' && poll.results !== null && poll.results.totalVotes > 0
      ? poll.results
      : null;

  return (
    <Card pad="md" bordered={false}>
      <Stack gap="md">
        <VisuallyHidden role="status" aria-live="polite">
          {revealed ? statusText : ''}
        </VisuallyHidden>

        <Row gap="xs" align="center" wrap>
          {poll.closed ? (
            <Pill size="sm" icon={I.clock}>
              {t('poll.closedPill')}
            </Pill>
          ) : timeLeft ? (
            <Pill size="sm" icon={I.clock}>
              {t(ENDS_IN_KEY[timeLeft.unit], { count: timeLeft.count })}
            </Pill>
          ) : null}
          {poll.visibility === 'public' ? (
            <Pill size="sm" icon={I.users}>
              {t('poll.publicVotes')}
            </Pill>
          ) : (
            <Pill size="sm" icon={I.lock}>
              {t('poll.anonymous')}
            </Pill>
          )}
        </Row>

        <div role="group" aria-label={question} className={styles.options}>
          {poll.options.map((o, i) => {
            const tally = tallyFor(o.id);
            const count = tally?.voteCount ?? 0;
            const share = pct(count, total);
            const mine = poll.viewerVotedOptionId === o.id;
            const lead = revealed && count === leadCount && count > 0;
            const voters = votersFor(o.id);
            const inert = pending || poll.closed;
            return (
              <Pressable
                key={o.id}
                className={styles.row}
                aria-pressed={mine}
                aria-disabled={inert || undefined}
                data-revealed={revealed || undefined}
                data-lead={lead || undefined}
                style={
                  {
                    '--share': share / 100,
                    '--delay': `${i * ROW_STAGGER_MS}ms`,
                  } as CSSProperties
                }
                onClick={() => {
                  if (inert || mine) return;
                  onVote(o.id);
                }}
              >
                <span className={styles.fill} aria-hidden="true" />
                <span className={styles.mark} aria-hidden="true">
                  <Icon size={12}>{I.check}</Icon>
                </span>
                <span className={styles.label}>
                  <Label>{o.label}</Label>
                </span>
                {revealed && voters.length > 0 ? (
                  <span className={styles.voters} aria-hidden="true">
                    <AvatarStack
                      size={24}
                      max={voters.length}
                      people={voters.map((v) => ({
                        name: v.firstName,
                        ...(v.photoUrl ? { photo: v.photoUrl } : {}),
                      }))}
                    />
                  </span>
                ) : null}
                {revealed ? (
                  <span className={styles.share}>
                    <Label
                      style={{
                        fontVariantNumeric: 'tabular-nums',
                        color: lead ? 'var(--palette-burgundy-deep)' : 'var(--muted-foreground)',
                      }}
                    >
                      {t('poll.percent', { pct: share })}
                    </Label>
                    <VisuallyHidden>
                      {', '}
                      {t('poll.optionVotes', { count })}
                      {lead ? `, ${t('poll.mostVotes')}` : ''}
                      {voters.length > 0
                        ? `, ${t('poll.votedBy', { names: voters.map((v) => v.firstName).join(', ') })}`
                        : ''}
                    </VisuallyHidden>
                  </span>
                ) : null}
              </Pressable>
            );
          })}
        </div>

        <Row gap="xs" align="center" wrap>
          <Caption>{statusText}</Caption>
          {canChangeVote ? (
            <>
              <DotSep />
              <Caption>{t('poll.changeVoteHint')}</Caption>
            </>
          ) : null}
          {votersResults !== null ? (
            <>
              <DotSep />
              <Button variant="link" size="sm" onClick={() => setVotersOpen(true)}>
                {t('poll.seeVoters')}
              </Button>
            </>
          ) : null}
        </Row>
      </Stack>
      {votersResults !== null && votersOpen ? (
        <PollVotersModal
          postId={postId}
          options={poll.options}
          results={votersResults}
          open={votersOpen}
          onOpenChange={setVotersOpen}
        />
      ) : null}
    </Card>
  );
}
